import { Box } from '@mui/material';
import { useCallback } from 'react';
import { PaymentForm } from '../../interfaces/Checkout/PaymentForm';
import { PaymentMethodCard } from './PaymentMethodCard';
import { PaymentMethod } from '../../interfaces/Checkout/PaymentMethod';
import { getEmptyCreditCardForm } from '../../interfaces/Checkout/CreditCardForm';
import { BankTransferContent } from './BankTransferContent';
import { BankTransferInstructions } from '../../interfaces/Checkout/BankTransferInstructions';
import { CreditCardContent } from './CreditCardContent';
import { ShippingMethod } from '../../interfaces/Shipping/ShippingMethod';
import { SelectShipping } from './SelectShipping';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type CheckoutPaymentProps = {
  form: PaymentForm;
  onChange: (newForm: PaymentForm) => void;
  shippingMethods: ShippingMethod[];
  bankTransferInstructions?: BankTransferInstructions;
  totalAmount: number;
  isFreeShipping: boolean;
};

export const CheckoutPayment = ({
  form,
  onChange,
  shippingMethods,
  bankTransferInstructions,
  totalAmount,
  isFreeShipping,
}: CheckoutPaymentProps) => {
  const t = useLocalizedText();

  const onChangeMethod = useCallback(
    (method: PaymentMethod) => {
      onChange({
        ...form,
        paymentMethod: method,
        creditCardForm: getEmptyCreditCardForm(),
      });
    },
    [form, onChange],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {shippingMethods.length > 0 && (
        <SelectShipping
          shippingMethods={shippingMethods}
          selectedShippingMethod={form.shippingMethod}
          onShippingMethodChange={(x) =>
            onChange({ ...form, shippingMethod: x })
          }
          isFreeShipping={isFreeShipping}
        />
      )}

      <PaymentMethodCard
        title={t('checkout.payment_method.credit_bank_card')}
        isSelected={form.paymentMethod === 'credit_card'}
        onSelect={() => onChangeMethod('credit_card')}
        content={
          <CreditCardContent
            form={form}
            onChange={(creditCardForm) => onChange({ ...form, creditCardForm })}
            totalAmount={totalAmount}
          />
        }
      />

      {!!bankTransferInstructions && bankTransferInstructions.enabled && (
        <PaymentMethodCard
          title={t('checkout.payment_method.bank_transfer')}
          isSelected={form.paymentMethod === 'bank_transfer'}
          onSelect={() => onChangeMethod('bank_transfer')}
          content={
            <BankTransferContent instructions={bankTransferInstructions} />
          }
        />
      )}
    </Box>
  );
};
